"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_BOX_SPLIT = exports.boxResetListQuerySchema = exports.BOX_RESET_REFUSALS = exports.resetBoxBalanceSchema = void 0;
exports.computeVariance = computeVariance;
exports.summariseBoxEntries = summariseBoxEntries;
exports.serializeBoxSplit = serializeBoxSplit;
exports.addBoxSplits = addBoxSplits;
exports.expectedCashInHand = expectedCashInHand;
exports.cashShortfallAgainstBox = cashShortfallAgainstBox;
exports.hasAccountBalanceComponent = hasAccountBalanceComponent;
const zod_1 = require("zod");
const money_1 = require("./money");
/* -------------------------------------------------------------------------- */
/* Write models                                                               */
/* -------------------------------------------------------------------------- */
/**
 * "Reset box balance" (§12.1).
 *
 * `expectedTotalMd` is an optimistic check, not decoration: with no approval
 * step, the only thing standing between the employee and an unnoticed
 * discrepancy is that the amount swept equals the amount they were shown. If a
 * payment lands between the screen rendering and the button being pressed, the
 * reset is refused and the employee re-reads the (now correct) figure rather
 * than signing off a number they never saw.
 */
exports.resetBoxBalanceSchema = zod_1.z
    .object({
    /** Decimal-string millidinars, as displayed. Omit to skip the check. */
    expectedTotalMd: zod_1.z
        .string()
        .trim()
        .regex(/^[+-]?\d+$/, 'Expected an integer millidinar amount.')
        .optional(),
    /**
     * The CASH figure the client displayed, in millidinars — signed, because a
     * refund-heavy shift can legitimately show negative expected cash.
     *
     * Variance is judged against cash, not the total, so guarding the total
     * alone is not enough: an offsetting cash-out/account-in pair leaves the
     * total untouched while moving the cash the employee counted against —
     * and would archive a false variance forever. REQUIRED whenever
     * `countedCashMd` is present (see the superRefine below); optional
     * otherwise for the count-skipped path.
     */
    expectedCashMd: zod_1.z
        .string()
        .trim()
        .regex(/^[+-]?\d+$/, 'Expected an integer millidinar amount.')
        .optional(),
    /**
     * What the employee physically counted, in millidinars (WAVE2-DESIGN §4).
     * Cash is whole IQD, so the value must divide by 1000 — this catches the
     * classic client bug of sending raw IQD (or double-converting) as a
     * validation error instead of a permanent 1000x variance in the audit log.
     * Optional on the wire for the count-skipped path; the dialog always sends
     * it together with `expectedCashMd`.
     */
    countedCashMd: zod_1.z
        .string()
        .trim()
        .regex(/^\d+$/, 'Expected a millidinar amount.')
        .refine((value) => BigInt(value) % 1000n === 0n, 'Counted cash must be whole IQD.')
        .optional(),
    note: zod_1.z.string().trim().max(300).optional(),
})
    .superRefine((value, ctx) => {
    // A count without the cash it was counted against is unjudgeable: the
    // server would have to guess which split the employee saw, and a guess
    // stored as a variance is exactly the false audit row this field prevents.
    if (value.countedCashMd != null && value.expectedCashMd == null) {
        ctx.addIssue({
            code: 'custom',
            path: ['expectedCashMd'],
            message: 'A counted reset must state the expected cash the client displayed.',
        });
    }
});
/**
 * counted - expected, both in millidinars. Kept beside expectedCashInHand so
 * the one variance rule lives in one place; the SERVICE stores the result
 * rather than recomputing it later (WAVE2-DESIGN §4 — the row records what
 * was agreed at the moment).
 */
function computeVariance(countedMd, expectedCashMd) {
    return countedMd - expectedCashMd;
}
/** New refusal: a nonzero variance demands one sentence of why. */
exports.BOX_RESET_REFUSALS = ['variance_note_required'];
/** Filters for the reset audit log (§12.1 "appear in reports as transfers"). */
exports.boxResetListQuerySchema = zod_1.z.object({
    employeeId: zod_1.z.string().min(1).optional(),
    /** ISO instants from date-time pickers; resolved to business dates server-side. */
    from: zod_1.z
        .string()
        .trim()
        .refine((value) => !Number.isNaN(Date.parse(value)), 'Expected an ISO date-time.')
        .optional(),
    to: zod_1.z
        .string()
        .trim()
        .refine((value) => !Number.isNaN(Date.parse(value)), 'Expected an ISO date-time.')
        .optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(500).default(50),
});
/**
 * Add up a set of box entries, keeping cash, account balance and electronic
 * payments apart.
 *
 * Pure and I/O-free so the server, the box screen and the reset confirmation
 * all derive the figure the same way — the split is the whole point of the
 * feature, and three implementations of it would eventually be three answers.
 */
function summariseBoxEntries(entries) {
    let cash = 0n;
    let account = 0n;
    let electronic = 0n;
    let entryCount = 0;
    for (const entry of entries) {
        const amount = (0, money_1.fromMillidinars)(entry.amountMd);
        // A switch with no default, so a fourth method is a compile error here
        // rather than money silently counted as notes in the drawer — which is
        // exactly what the old if/else would have done with ELECTRONIC.
        switch (entry.method) {
            case 'CASH':
                cash += amount;
                break;
            case 'ACCOUNT_BALANCE':
                account += amount;
                break;
            case 'ELECTRONIC':
                electronic += amount;
                break;
        }
        entryCount += 1;
    }
    return {
        totalMd: (cash + account + electronic),
        cashMd: cash,
        accountBalanceMd: account,
        electronicMd: electronic,
        entryCount,
    };
}
/** {@link BoxSplitAmounts} as the decimal strings that cross the wire. */
function serializeBoxSplit(amounts) {
    return {
        totalMd: amounts.totalMd.toString(),
        cashMd: amounts.cashMd.toString(),
        accountBalanceMd: amounts.accountBalanceMd.toString(),
        electronicMd: amounts.electronicMd.toString(),
        entryCount: amounts.entryCount,
    };
}
/** Add two splits — used to total a list of employee boxes or resets. */
function addBoxSplits(a, b) {
    return {
        totalMd: (a.totalMd + b.totalMd),
        cashMd: (a.cashMd + b.cashMd),
        accountBalanceMd: (a.accountBalanceMd + b.accountBalanceMd),
        electronicMd: (a.electronicMd + b.electronicMd),
        entryCount: a.entryCount + b.entryCount,
    };
}
/** An empty split — the identity for {@link addBoxSplits}. */
exports.EMPTY_BOX_SPLIT = {
    totalMd: 0n,
    cashMd: 0n,
    accountBalanceMd: 0n,
    electronicMd: 0n,
    entryCount: 0,
};
/**
 * What should physically be in the drawer for this box.
 *
 * Exactly the cash component: the account-balance and electronic components
 * never existed as notes. Named rather than inlined because "the box total minus the account
 * part" is the sentence an employee has to be told at handover, and it should
 * read the same way in the code.
 */
function expectedCashInHand(split) {
    return split.cashMd;
}
/**
 * How far the drawer will be below the box total — the number the UI must show
 * so the figure can be reconciled. Zero when the employee took only cash.
 */
function cashShortfallAgainstBox(split) {
    return (0, money_1.subtract)(split.totalMd, split.cashMd);
}
/** True when the box contains account-balance payments, so the UI must explain itself. */
function hasAccountBalanceComponent(split) {
    return split.accountBalanceMd !== 0n;
}
//# sourceMappingURL=box-balance.js.map